// Coach read-path projection (CO-11 privacy gate).
//
// When a coach reads an ASSIGNED athlete's data cross-user (edge-gated via
// resolveCoachReadTarget), the workout substance — volume / e1RM / hard-sets /
// reps — flows on the active edge alone. Two things do NOT:
//
//  1. HEALTH SPECIAL-CATEGORY data — heart rate and energy (calories). GDPR /
//     Swiss LPD: these may leave to the coach ONLY within an explicit
//     per-metric consent scope. That scaffold does NOT exist yet
//     (CoachAthleteEdge is a flat active/invited/revoked relationship, no
//     per-metric grain — see CO-9 / HC-6 / HC-7), so the cross-user read path
//     strips them unconditionally.
//
//  2. WHERE the athlete trains — the `location:` tag on a LogSet (EQ-1).
//     Antenore's decision (2026-08-11, equipment-model.md §6.3): *"Il tag
//     palestra no, ma il tag macchina sì"*. The gym is a fact about the
//     athlete's life, not about the training, and the coach has no use for it.
//
// ⛔ Why this module was renamed from coachHealthProjection (2026-08-13):
// §6.3 recorded the gym rule as CLOSED and pointed at pickCoachProfileFields,
// which governs the PROFILE. The gym tag rides the SET, and the set path
// stripped health fields only — so the rule was written but never applied. It
// leaked nothing solely because nothing wrote `location` yet; EQ-1 slice D is
// what turns the value on. A module named for one of the two vocabularies it
// owns is how the second one gets forgotten again.
//
// ⭐ `equipment-instance:` deliberately SURVIVES. §6.3 wants it to reach the
// coach — it explains why the same load costs different effort, the RPE/RIR
// bridge — and it is anonymous BY CONSTRUCTION: the stored value is always a
// canonical `machine-a` slug, never free text (the automatic-names decision,
// 2026-08-13). So §6.3's "anonymised before the coach sees it" is already
// satisfied upstream, and nothing has to be rewritten here.
//
// This is the ONE place both vocabularies live. A future HC field (glucose,
// sleep) that rides a Session or LogSet, or a future namespace that names a
// place or a person, gets added to the lists here — one line — not hunted for
// across every route body. Self-reads (targetSub === callerSub) never call
// these; they return everything.
//
// Pure, RN/web-portable, no DDB import.

import type { Session } from "./schemas/session.js";
import type { LogSet } from "./schemas/logSet.js";
import { LOCATION_NS } from "./schemas/tag.js";
import { isAutoLocationValue } from "./equipmentInstances.js";

// Session-level health special-category fields (session.ts:68-93).
export const SESSION_HEALTH_FIELDS = [
  "calories",
  "caloriesEstimate",
  "avgHrBpm",
  "peakHrBpm",
] as const satisfies readonly (keyof Session)[];

// Cardio LogSet health special-category fields (logSet.ts:121-122).
export const LOGSET_HEALTH_FIELDS = [
  "avgHrBpm",
  "calories",
] as const satisfies readonly (keyof LogSet)[];

// Tag namespaces a coach must never see on a cross-user read. Referenced
// from the shared LOCATION_NS constant rather than the literal "location",
// so the seam that WRITES the tag (workoutSplit) and the seam that hides it
// cannot drift apart.
export const COACH_PRIVATE_TAG_NAMESPACES = [LOCATION_NS] as const;

const omitKeys = <T extends object, K extends keyof T>(
  obj: T,
  keys: readonly K[],
): Omit<T, K> => {
  const out = { ...obj };
  for (const k of keys) delete out[k];
  return out;
};

/** Drop session-level HR/calories for a coach cross-user read. */
export const stripSessionHealthFields = (session: Session): Session =>
  omitKeys(session, SESSION_HEALTH_FIELDS) as Session;

/** Drop cardio-set HR/calories for a coach cross-user read. */
export const stripLogSetHealthFields = (set: LogSet): LogSet =>
  omitKeys(set, LOGSET_HEALTH_FIELDS) as LogSet;

/**
 * Drop the tags that NAME where the athlete trained.
 *
 * Matches on the namespace prefix, so `location:dobedan` goes and a catalog
 * tag that merely CONTAINS the word does not. A set left with no tags at all
 * drops the field rather than carrying an empty array, matching how the write
 * seam omits it — otherwise a coach read and a self read of the same set
 * would differ by a `tags: []` that means nothing.
 *
 * ⭐ An OPAQUE place id survives (Antenore's design, 2026-08-13). `place-a`
 * names nothing: it lets a coach see that two sessions happened at different
 * places — which is what explains a load difference — without learning where
 * either was. Hiding the tag outright would throw that signal away along with
 * the secret. Only a value that could BE a name is removed.
 *
 * ⛔ Nothing mints those ids yet (the name store is EQ-1-PLACEID), so today
 * every location tag is a name and every one of them is dropped. The test for
 * it is here now precisely so the gate is already correct on the day they
 * start being minted, rather than being remembered then.
 */
export const stripLogSetPrivateTags = (set: LogSet): LogSet => {
  if (!set.tags) return set;
  const kept = set.tags.filter((tag) => {
    const ns = COACH_PRIVATE_TAG_NAMESPACES.find((n) =>
      tag.startsWith(`${n}:`),
    );
    if (ns === undefined) return true;
    return isAutoLocationValue(tag.slice(ns.length + 1));
  });
  if (kept.length === set.tags.length) return set;
  const out = { ...set };
  if (kept.length === 0) delete out.tags;
  else out.tags = kept;
  return out;
};

/**
 * THE cross-user projection for a Session envelope. One entry point so a
 * caller cannot apply half of the gate.
 */
export const projectSessionForCoach = (session: Session): Session =>
  stripSessionHealthFields(session);

/**
 * THE cross-user projection for a LogSet — health fields AND the place.
 *
 * Callers use this rather than the individual strips: the two rules have
 * different reasons but the same trigger (`crossUser`), and a route that
 * remembers one and forgets the other is exactly the failure this module
 * was renamed for.
 */
export const projectLogSetForCoach = (set: LogSet): LogSet =>
  stripLogSetPrivateTags(stripLogSetHealthFields(set));
